import { useNavigate } from 'react-router-dom';
import { Container, Button, Spinner, Modal, Form } from 'react-bootstrap';
import { ChevronRight, Clipboard, ClipboardCheckFill, BoxArrowUp } from 'react-bootstrap-icons';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import './NewCountryPage.css';
import { useEffect, useState, useCallback } from 'react';
import IntegratedSearchNavBar from '../navigation/IntegratedSearchNavBar';
import BottomNavbar from '../navigation/Menu';
import useFeedData from '../../hooks/useFeedData';
import useCollections, { CollectionModal } from '../../hooks/useCollections';

import { 
  getCountryConfig, 
  getCountryInfo, 
  getHomePageFeeds,
  hasNewsItems
} from '../../utils/countryConfig';
import openCapacitorSite from '../../utils/browserUtils';
import FeedItem from '../feed/FeedItem';

// Helper functions
const handleHapticsClick = async (callback) => {
  try {
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch (error) {
    // Haptics not available, continue without it
  }
  if (callback) callback();
  window.scrollTo(0, 0);
};

const NewCountryHomePage = () => {
  const navigate = useNavigate();

  // Get current country from URL path automatically
  const getCurrentCountrySlug = () => {
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    return pathSegments[0] || 'australia';
  };

  const currentCountrySlug = getCurrentCountrySlug();
  const currentCountryConfig = getCountryConfig(currentCountrySlug);
  const currentCountryInfo = getCountryInfo(currentCountrySlug);
  const countryFeeds = getHomePageFeeds(currentCountrySlug);

  // State for feed data
  const [feedLength, setFeedLength] = useState(30);
  const [hasMoreData, setHasMoreData] = useState(true);
  
  // Search state management
  const [searchQuery, setSearchQuery] = useState('');
  const [searchPerformed, setSearchPerformed] = useState(false);
  
  // Use the collection hook for collection functionality
  const {
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    closeModal,
    handleSave,
    isStorageAvailable,
  } = useCollections();
  
  // Share modal states
  const [shareModalIsOpen, setShareModalIsOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareLink, setShareLink] = useState('');

  // Integrated search handler
  const handleIntegratedSearch = useCallback((query) => {
    const trimmedQuery = query.trim();
    const isSearching = !!trimmedQuery;
    
    setSearchQuery(trimmedQuery);
    setSearchPerformed(isSearching);
    
    // Reset feed length when starting a new search
    if (isSearching && !searchPerformed) {
      setFeedLength(30);
    }
  }, [searchPerformed]);

  // Handle Copy Link
  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(shareLink).then(() => {
      setLinkCopied(true);
    }).catch((error) => {
      console.error('Error copying to clipboard:', error);
    });
  }, [shareLink]);

  // Handle Govstr Copy Link
  const handleGovstrCopyLink = useCallback(() => {
    openCapacitorSite("https://govstr.app/compose?content="+shareLink, "_blank");
    navigator.clipboard.writeText(shareLink);
  }, [shareLink]);

  // Handle Native Share
  const handleNativeShare = useCallback(async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: '',
          text: '',
          url: shareLink,
        });
        return;
      }
      
      const { Share } = await import('@capacitor/share');
      await Share.share({
        title: '',
        text: '',
        url: shareLink,
        dialogTitle: 'Share with',
      });
    } catch (error) {
      console.error('Error sharing:', error);
    }
  }, [shareLink]);

  // Handle Load More
  const handleLoadMore = useCallback(() => {
    const scrollPosition = window.scrollY;
    setFeedLength((prevLength) => prevLength + 20);
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
  }, []);

  // Handle recents link click - navigates to FullFeedPage with proper URL format
  const handleRecentsClick = useCallback((feed) => {
    if (!hasNewsItems(currentCountrySlug)) {
      return;
    }
    
    handleHapticsClick(() => {});
    window.scrollTo(0, 0);
    
    // Parse apiParams to extract schema, table, and filters
    const params = new URLSearchParams(feed.apiParams.replace(/^&/, ''));
    const schema = params.get('schema') || 'public';
    const table = params.get('table') || 'news_items';
    const filterColumn = params.get('filter_column');
    const filterString = params.get('filter_string');
    const orderBy = params.get('order_by');
    
    // Build the URL with query parameters
    let url = `/feed/${schema}/${table}`;
    const queryParams = new URLSearchParams();
    
    if (filterColumn) queryParams.set('filterColumn', filterColumn);
    if (filterString) queryParams.set('filterString', filterString);
    if (orderBy) queryParams.set('orderBy', orderBy);
    
    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }
    
    navigate(url);
  }, [currentCountrySlug, navigate]);

  // Helper function to format country name for display
  const formatCountryName = useCallback((countrySlug) => {
    return countrySlug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, []);

  // Update localStorage when country changes
  useEffect(() => {
    localStorage.setItem('currentPage', currentCountrySlug);
    localStorage.setItem('lastCountry', currentCountrySlug);
  }, [currentCountrySlug]);

  // Fallback if country not found
  if (!currentCountryConfig || !currentCountryInfo) {
    return (
      <div id='homebg'>
        <BottomNavbar/>
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px',
          color: 'var(--text-primary, #FFFFFF)',
          fontSize: '18px'
        }}>
          Country not found: {currentCountrySlug}
        </div>
      </div>
    );
  }

  return (
    <div id='homebg'>
      {/* Using IntegratedSearchNavBar with search functionality */}
      <IntegratedSearchNavBar
        searchQuery={searchQuery}
        onSearchChange={handleIntegratedSearch}
        currentCountry={currentCountrySlug}
        placeholder={`Search ${formatCountryName(currentCountrySlug)} news`}
      />
      <br></br>
      <BottomNavbar/>

      <div className="country-page-content">
        {/* Render feeds based on configuration */}

        {countryFeeds.map((feed) => (
          <FeedSection 
            key={feed.id} 
            feed={feed}
            countryInfo={currentCountryInfo}
            feedLength={feedLength}
            searchQuery={searchQuery}
            searchPerformed={searchPerformed}
            onRecentsClick={handleRecentsClick}
            onLoadMore={handleLoadMore}
            hasMoreData={hasMoreData}
            setHasMoreData={setHasMoreData}
          />
        ))}

        {/* Special message for countries with no content */}
        {!hasNewsItems(currentCountrySlug) && currentCountryInfo.specialMessage && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: 'var(--text-primary, #FFFFFF)',
            fontSize: '18px'
          }}>
            {currentCountryInfo.specialMessage}
          </div>
        )}

        <br />

        {/* Note for all countries */}
        <div style={{ 
          textAlign: 'center', 
          padding: '20px',
          color: 'var(--text-primary, #FFFFFF)',
          fontSize: '14px',
          fontStyle: 'italic'
        }}>
          More news coming soon
        </div>
      </div>
      
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />

      {/* Collection Modal - from useCollections hook */}
      <CollectionModal
        isModalOpen={isModalOpen}
        currentItem={currentItem}
        saveSuccess={saveSuccess}
        note={note}
        setNote={setNote}
        handleSave={handleSave}
        closeModal={closeModal}
        isStorageAvailable={isStorageAvailable}
      />

      {/* Share Modal */}
      <Modal 
        id="fullBillModal" 
        show={shareModalIsOpen} 
        size="xl" 
        aria-labelledby="contained-modal-title-vcenter" 
        centered 
        onHide={() => setShareModalIsOpen(false)}
      >
        <Modal.Header className="feed-modal-header" closeButton>
          <h3>Share</h3>
        </Modal.Header>
        <Modal.Body className="feed-modal-body">
          <Form.Group id='noteModalForm'>
            <Form.Control
              className="feed-modal-input"
              type="text"
              readOnly
              value={shareLink}
            />
          </Form.Group>
          <Modal.Footer className="feed-modal-footer">
            <Button className="feed-button me-2" onClick={handleGovstrCopyLink}>
              Govstr
            </Button>

            <Button className="feed-button me-2" onClick={handleCopyLink}>
              {linkCopied ? <ClipboardCheckFill /> : <Clipboard />}
            </Button>

            <Button className="feed-button" onClick={handleNativeShare}>
              <BoxArrowUp />
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </Modal>

      <style>{`
        /* Match the Glance column width on the homepage so feed cards read as
           cards rather than full-width bars on wide desktop screens. */
        .country-page-content {
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          padding-left: 10px;
          padding-right: 10px;
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
};

/**
 * Feed Section Component
 */
const FeedSection = ({ 
  feed, 
  countryInfo, 
  feedLength, 
  searchQuery,
  searchPerformed,
  onRecentsClick, 
  onLoadMore,
  hasMoreData,
  setHasMoreData,
}) => {
  // Parse schema and table from apiParams for FeedItem
  const params = new URLSearchParams(feed.apiParams.replace(/^&/, ''));
  const schema = params.get('schema') || 'public';
  const table = params.get('table') || 'news_items';

  // Pass search parameters to useFeedData hook
  const { data, loading, error } = useFeedData(
    feed.apiParams, 
    feedLength, 
    searchQuery, 
    searchPerformed
  );

  // Update hasMoreData when data changes
  useEffect(() => {
    if (data && feed.type === 'news') {
      setHasMoreData(data.length === feedLength);
    }
  }, [data, feedLength, feed.type, setHasMoreData]);

  // Handle section header click
  const handleSectionHeaderClick = () => {
    if (!countryInfo.hasNewsItems) {
      return;
    }
    onRecentsClick(feed);
  };

  // Filter data based on search query if searching
  const filteredData = searchPerformed && searchQuery 
    ? data?.filter(item => {
        const titleMatch = (item.title || item.status_title || '').toLowerCase().includes(searchQuery.toLowerCase());
        const descriptionMatch = (item.description || '').toLowerCase().includes(searchQuery.toLowerCase());
        return titleMatch || descriptionMatch;
      })
    : data;

  return (
    <div>
      {/* Show search results header when searching */}
      {searchPerformed && searchQuery ? (
        <div>
          <h5 className="carousel-heading">
            <div className="title-container">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
              </svg>
              <span className="heading-text">Search Results for "{searchQuery}" ({filteredData?.length || 0} results)</span>
            </div>
          </h5>
        </div>
      ) : (
        /* Original section header */
        countryInfo.hasNewsItems ? (
          <div onClick={handleSectionHeaderClick} style={{ cursor: 'pointer' }}>
            {/* <h5 className="carousel-heading">
              <div className="title-container">
    
  
              </div>
            </h5> */}
          </div>
        ) : (
          <div style={{ cursor: 'pointer' }}>
            {/* <h5 className="carousel-heading">
              <div className="title-container">
           
                
              </div>
            </h5> */}
          </div>
        )
      )}
      
      <div id='homeRecentSpacer'></div>

      {/* Loading Spinner */}
      {loading && (
        <div className="d-flex justify-content-center align-items-center">
          <Spinner id='spinner' animation="grow" variant="light" />
        </div>
      )}

      {/* Feed Items - Using FeedItem component with proper padding */}
      <div className="country-feed-items-container">
        {!loading && filteredData && filteredData.map((item, index) => (
          <div key={item.id || index} className="country-feed-item-wrapper">
            <FeedItem
              item={item}
              feedName={feed.feedName || feed.name}
              branch={feed.branch || 'N/A'}
              topic={feed.topic || 'N/A'}
              buttonName={feed.buttonName || 'Doc'}
              schema={schema}
              table={table}
              variant="default"
              isRecentsItem={false}
            />
          </div>
        ))}
      </div>

      {/* Show "no results" message for search */}
      {!loading && searchPerformed && searchQuery && (!filteredData || filteredData.length === 0) && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px',
          color: 'var(--text-primary, #FFFFFF)',
          fontSize: '16px'
        }}>
          No results found for "{searchQuery}"
        </div>
      )}

      {/* Load More Button */}
      {!loading && filteredData && filteredData.length > 0 && hasMoreData && feed.type === 'news' && !searchPerformed && (
        <>
          <div style={{ height: '20px' }}></div>
          <div className="d-flex justify-content-center align-items-center">
            <Button className="feed-button view-more-button" onClick={onLoadMore}>
              More
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default NewCountryHomePage;